import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { orderService } from '../services/orderService';
import { Order, OrderItem } from '../types';
import { getProductImageUrl } from '../utils/imageUtils';

interface OrderDetails extends Order {
    items: OrderItem[];
    first_name: string;
    last_name: string;
    email: string;
}

const OrderDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [order, setOrder] = useState<OrderDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (id) {
            fetchOrderDetails();
        }
    }, [id]);

    const fetchOrderDetails = async () => {
        try {
            setLoading(true);
            const response = await orderService.getOrderById(parseInt(id!));
            setOrder(response.order);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Erreur lors du chargement de la commande');
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(price);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'confirmed':
                return 'bg-blue-100 text-blue-800';
            case 'preparing':
                return 'bg-purple-100 text-purple-800';
            case 'shipped':
                return 'bg-indigo-100 text-indigo-800';
            case 'delivered':
                return 'bg-green-100 text-green-800';
            case 'cancelled':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'pending':
                return 'En attente';
            case 'confirmed':
                return 'Confirmée';
            case 'preparing':
                return 'En préparation';
            case 'shipped':
                return 'Expédiée';
            case 'delivered':
                return 'Livrée';
            case 'cancelled':
                return 'Annulée';
            default:
                return status;
        }
    };

    const getDeliveryTimeText = (time: string) => {
        switch (time) {
            case 'morning':
                return 'Matin (8h-12h)';
            case 'afternoon':
                return 'Après-midi (12h-17h)';
            case 'evening':
                return 'Soir (17h-20h)';
            default:
                return time;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-eldai-blue text-xl">Chargement de la commande...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-eldai-gray-light">
                <div className="container mx-auto px-4 py-8">
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        {error}
                    </div>
                    <div className="mt-4">
                        <Link
                            to="/orders"
                            className="text-eldai-blue hover:underline"
                        >
                            ← Retour aux commandes
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="min-h-screen bg-eldai-gray-light">
                <div className="container mx-auto px-4 py-8">
                    <div className="text-center py-16">
                        <h2 className="text-2xl font-bold text-eldai-blue mb-4">
                            Commande non trouvée
                        </h2>
                        <Link
                            to="/orders"
                            className="text-eldai-blue hover:underline"
                        >
                            Retour aux commandes
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-eldai-gray-light">
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <Link
                        to="/orders"
                        className="text-eldai-blue hover:underline mb-4 inline-block"
                    >
                        ← Retour aux commandes
                    </Link>
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-eldai-blue mb-2">
                                Commande #{order.order_number}
                            </h1>
                            <p className="text-eldai-gray">
                                Passée le {formatDate(order.created_at)}
                            </p>
                        </div>
                        <div className="mt-4 lg:mt-0">
                            <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                                {getStatusText(order.status)}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Order Details */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Order Items */}
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-xl font-bold text-eldai-blue mb-4">
                                Articles commandés
                            </h2>
                            <div className="space-y-4">
                                {order.items.map((item) => (
                                    <div key={item.id} className="flex items-center justify-between border-b pb-4">
                                        <div className="flex items-center space-x-4">
                                            {item.photo_url && (
                                                <img
                                                    src={item.photo_url}
                                                    alt={item.product_designation}
                                                    className="w-16 h-16 object-cover rounded-lg"
                                                />
                                            )}
                                            <div>
                                                <h3 className="font-semibold text-eldai-blue">
                                                    {item.product_designation}
                                                </h3>
                                                <p className="text-sm text-eldai-gray">
                                                    Prix unitaire: {formatPrice(item.unit_price)}
                                                </p>
                                                {item.cacherout && (
                                                    <p className="text-xs text-green-600">
                                                        ✓ {item.cacherout}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-semibold text-eldai-blue">
                                                {formatPrice(item.total_price)}
                                            </div>
                                            <div className="text-sm text-eldai-gray">
                                                Quantité: {item.quantity}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Delivery Information */}
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-xl font-bold text-eldai-blue mb-4">
                                Informations de livraison
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <h3 className="font-semibold text-eldai-blue mb-2">Adresse</h3>
                                    <p className="text-eldai-gray">
                                        {order.delivery_address}<br />
                                        {order.delivery_postal_code} {order.delivery_city}
                                    </p>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-eldai-blue mb-2">Contact</h3>
                                    <p className="text-eldai-gray">
                                        📞 {order.delivery_phone}
                                    </p>
                                </div>
                                {order.preferred_delivery_date && (
                                    <div>
                                        <h3 className="font-semibold text-eldai-blue mb-2">Date préférée</h3>
                                        <p className="text-eldai-gray">
                                            📅 {new Date(order.preferred_delivery_date).toLocaleDateString('fr-FR')}
                                        </p>
                                    </div>
                                )}
                                {order.preferred_delivery_time && (
                                    <div>
                                        <h3 className="font-semibold text-eldai-blue mb-2">Créneau</h3>
                                        <p className="text-eldai-gray">
                                            🕐 {getDeliveryTimeText(order.preferred_delivery_time)}
                                        </p>
                                    </div>
                                )}
                            </div>
                            {order.delivery_notes && (
                                <div className="mt-4">
                                    <h3 className="font-semibold text-eldai-blue mb-2">Instructions</h3>
                                    <p className="text-eldai-gray bg-eldai-gray-light p-3 rounded">
                                        {order.delivery_notes}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Order Summary */}
                    <div className="space-y-6">
                        {/* Customer Info */}
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-xl font-bold text-eldai-blue mb-4">
                                Client
                            </h2>
                            <div className="space-y-2">
                                <p className="text-eldai-gray">
                                    <strong>Nom:</strong> {order.first_name} {order.last_name}
                                </p>
                                <p className="text-eldai-gray">
                                    <strong>Email:</strong> {order.email}
                                </p>
                            </div>
                        </div>

                        {/* Order Total */}
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-xl font-bold text-eldai-blue mb-4">
                                Récapitulatif
                            </h2>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span>Articles ({order.total_items})</span>
                                    <span>{formatPrice(order.total_amount)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Livraison</span>
                                    <span className="text-green-600">Gratuite</span>
                                </div>
                                <div className="border-t pt-2">
                                    <div className="flex justify-between text-lg font-bold">
                                        <span>Total</span>
                                        <span className="text-eldai-blue">{formatPrice(order.total_amount)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Payment Method */}
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-xl font-bold text-eldai-blue mb-4">
                                Paiement
                            </h2>
                            <div className="bg-eldai-yellow p-4 rounded-lg">
                                <div className="flex items-center space-x-2">
                                    <span className="text-2xl">💳</span>
                                    <div>
                                        <h3 className="font-semibold text-eldai-blue">
                                            Paiement à la livraison
                                        </h3>
                                        <p className="text-sm text-eldai-gray">
                                            Espèces ou carte bancaire
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderDetailsPage;
